import { ChangeEvent, ReactNode, useLayoutEffect, useRef, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import { formatLive } from "./amountFormat";

const formatAmountDisplay = (value: string): string => {
  const num = parseFloat(value.replace(",", "."));
  if (Number.isNaN(num)) return "";
  return new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const AmountField = ({
  label = "",
  value,
  onChange,
  liveFormat,
  error,
  helperText,
  sx,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  liveFormat?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  sx?: object;
}) => {
  const [focused, setFocused] = useState(false);
  const displayValue = liveFormat
    ? formatLive(value).display
    : focused
      ? value
      : value
        ? formatAmountDisplay(value)
        : "";

  // Po przeformatowaniu „na żywo" przywracamy kursor wg liczby znaczących znaków
  // (z pominięciem spacji = separatorów tysięcy) przed kursorem.
  const pendingCaret = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (!liveFormat || pendingCaret.current == null) return;
    const el = inputRef.current;
    if (!el) return;
    const target = pendingCaret.current;
    pendingCaret.current = null;
    let sig = 0;
    let pos = 0;
    const d = el.value;
    for (; pos < d.length; pos++) {
      if (d[pos] !== " ") sig++;
      if (sig >= target) {
        pos++;
        break;
      }
    }
    el.setSelectionRange(pos, pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (liveFormat) {
      const input = e.target;
      const caret = input.selectionStart ?? input.value.length;
      pendingCaret.current = input.value.slice(0, caret).replace(/\s/g, "").length;
      onChange(formatLive(input.value).stored);
    } else {
      onChange(e.target.value.replace(/[^\d.,]/g, ""));
    }
  };

  return (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={displayValue}
      error={error}
      helperText={helperText}
      sx={sx}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={handleChange}
      inputRef={inputRef}
      InputProps={{
        endAdornment: <InputAdornment position="end">zł</InputAdornment>,
      }}
      inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
    />
  );
};

export default AmountField;
